import React, { useState } from 'react';
import { CompleteKundali, Language } from '../../types/kundali';
import { LifeThemeId } from '../../types/advancedLifeAnalysis';
import { generateTemporalSynthesis } from '../../utils/v518/temporalSynthesisEngine';
import { TemporalQuarterWindow } from '../../types/v518';
import { 
  Calendar, 
  Clock, 
  TrendingUp, 
  AlertTriangle, 
  CheckCircle2, 
  ShieldCheck, 
  Sparkles, 
  Compass, 
  ChevronRight,
  Info,
  Layers
} from 'lucide-react';

interface TemporalWindowCardProps {
  kundali: CompleteKundali;
  themeId?: LifeThemeId;
  lang?: Language;
}

export const TemporalWindowCard: React.FC<TemporalWindowCardProps> = ({
  kundali,
  themeId = 'career_job',
  lang = 'en'
}) => {
  const [horizonMonths, setHorizonMonths] = useState<12 | 24 | 36>(12);
  const isHi = lang === 'hi';

  const synthesis = generateTemporalSynthesis(kundali, themeId, horizonMonths, lang);

  const getBadgeStyle = (classification: TemporalQuarterWindow['classification']) => {
    switch (classification) {
      case 'SUPPORTIVE':
        return 'bg-emerald-50 text-emerald-800 border-emerald-300';
      case 'MODERATE / STEADY':
        return 'bg-[#FAF6ED] text-astro-accent border-astro-accent/40';
      case 'CONSTRAINED / CAUTIONARY':
        return 'bg-amber-50 text-amber-900 border-amber-300';
      default:
        return 'bg-[#FAF6ED] text-astro-secondary-text border-astro-border';
    }
  };

  const getClassificationLabel = (classification: TemporalQuarterWindow['classification']) => {
    if (isHi) {
      switch (classification) {
        case 'SUPPORTIVE':
          return 'अत्यंत अनुकूल व फलदायी';
        case 'MODERATE / STEADY':
          return 'संतुलित व प्रगतिशील';
        case 'CONSTRAINED / CAUTIONARY':
          return 'धैर्य व सजगता का समय';
        default:
          return 'सामान्य प्रभाव';
      }
    }
    switch (classification) {
      case 'SUPPORTIVE':
        return 'Highly Supportive';
      case 'MODERATE / STEADY':
        return 'Steady & Progressive';
      case 'CONSTRAINED / CAUTIONARY':
        return 'Patience & Mindful Action';
      default:
        return 'Neutral Period';
    }
  };

  return (
    <div
      id="v518-temporal-window-card"
      className="bg-astro-bg border border-astro-border rounded-2xl p-5 sm:p-7 shadow-xs space-y-6"
    >
      {/* 1. Card Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-astro-border/80">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-bold uppercase tracking-wider bg-astro-primary/10 text-astro-primary px-2.5 py-0.5 rounded-full border border-astro-primary/20 flex items-center gap-1">
              <Clock className="w-3 h-3 text-astro-accent" />
              {isHi ? 'समय चक्र व ग्रहीय गति' : 'Time Horizons & Cosmic Cycles'}
            </span>
            <span className="text-xs text-astro-secondary-text">
              {isHi ? 'आगामी समय की ग्रहीय अनुकूलता' : 'Upcoming Planetary Momentum'}
            </span>
          </div>
          <h3 className="text-xl sm:text-2xl font-bold font-serif text-astro-text mt-1.5">
            {isHi ? 'आपके आगामी समय चक्र व ग्रहीय अनुकूलता' : 'Your Upcoming Time Horizons & Planetary Momentum'}
          </h3>
          <p className="text-xs text-astro-secondary-text mt-0.5">
            {isHi 
              ? 'आपकी वर्तमान दशा, महत्वपूर्ण गोचर व सर्वाष्टकवर्ग ऊर्जा का संयुक्त व्यावहारिक मार्गदर्शन' 
              : 'Synthesizing your active life cycles (Dasha), key transits (Gochar), and energetic support to guide your milestones'}
          </p>
        </div>

        {/* Horizon Selector (1 Year, 2 Years, 3 Years) */}
        <div className="flex items-center gap-1.5 bg-[#FAF6ED] p-1.5 rounded-xl border border-astro-border self-start sm:self-auto shrink-0 shadow-2xs">
          {([12, 24, 36] as const).map((months) => {
            const labelEn = months === 12 ? '1 Year Horizon' : months === 24 ? '2 Year Horizon' : '3 Year Horizon';
            const labelHi = months === 12 ? '१ वर्ष की दृष्टि' : months === 24 ? '२ वर्ष की दृष्टि' : '३ वर्ष की दृष्टि';
            const isSelected = horizonMonths === months;
            return (
              <button
                key={months}
                id={`horizon-btn-${months}`}
                type="button"
                onClick={() => setHorizonMonths(months)}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                  isSelected
                    ? 'bg-astro-primary text-white shadow-xs'
                    : 'text-astro-secondary-text hover:text-astro-primary hover:bg-astro-bg'
                }`}
              >
                {isHi ? labelHi : labelEn}
              </button>
            );
          })}
        </div>
      </div>

      {/* 2. Synthesis Overview Banner (3 Clean Cards) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
        {/* Overall Climate */}
        <div className="p-4 rounded-xl bg-[#FAF6ED] border border-astro-border flex flex-col justify-between">
          <div className="text-xs text-astro-secondary-text font-semibold">
            {isHi ? 'समय चक्र की सामान्य प्रकृति' : 'Overall Horizon Energy'}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <span className={`px-2.5 py-1 text-xs font-bold rounded-full border ${getBadgeStyle(synthesis.overallClassification)}`}>
              {getClassificationLabel(synthesis.overallClassification)}
            </span>
            <span className="text-xs font-bold text-astro-primary">
              {synthesis.averageScore}% {isHi ? 'अनुकूल' : 'Auspicious'}
            </span>
          </div>
        </div>

        {/* Most Supportive Quarter */}
        <div className="p-4 rounded-xl bg-[#FAF6ED] border border-astro-border flex flex-col justify-between">
          <div className="text-xs text-astro-secondary-text font-semibold">
            {isHi ? 'सर्वाधिक फलदायी समय' : 'Most Auspicious Window'}
          </div>
          <div className="mt-2 text-xs font-bold text-astro-primary flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-astro-accent shrink-0" />
            <span>{isHi ? synthesis.bestWindow.labelHi : synthesis.bestWindow.labelEn}</span>
          </div>
        </div>

        {/* Mindful / Discipline Quarter */}
        <div className="p-4 rounded-xl bg-[#FAF6ED] border border-astro-border flex flex-col justify-between">
          <div className="text-xs text-astro-secondary-text font-semibold">
            {isHi ? 'धैर्य व एकाग्रता का समय' : 'Mindful Focus Period'}
          </div>
          <div className="mt-2 text-xs font-bold text-astro-secondary-text flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4 text-astro-accent shrink-0" />
            <span>{isHi ? synthesis.challengingWindow.labelHi : synthesis.challengingWindow.labelEn}</span>
          </div>
        </div>
      </div>

      {/* 3. Natal vs Timing Alignment Note */}
      <div className="p-4 rounded-xl bg-[#FAF6ED]/80 border border-astro-accent/40 text-xs text-astro-text flex items-start gap-3 shadow-2xs">
        <div className="p-1.5 rounded-lg bg-astro-primary text-white shrink-0 mt-0.5">
          <Compass className="w-4 h-4 text-astro-accent" />
        </div>
        <div className="space-y-0.5">
          <span className="font-bold font-serif text-sm text-astro-primary block">
            {isHi ? 'जन्मजात क्षमता एवं समय चक्र का तालमेल' : 'Harmonizing Your Birth Potential & Planetary Timing'}
          </span>
          <p className="text-xs text-astro-secondary-text leading-relaxed">
            {isHi ? synthesis.natalPromiseVsTimingSummary.summaryHi : synthesis.natalPromiseVsTimingSummary.summaryEn}
          </p>
        </div>
      </div>

      {/* 4. Quarterly Timeline Windows */}
      <div className="space-y-3 pt-1">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-bold text-astro-primary font-serif">
            {isHi ? 'तिमाही समय अवधियां व महत्वपूर्ण पड़ाव' : 'Seasonal Periods & Practical Milestones'}
          </h4>
          <span className="text-xs text-astro-secondary-text">
            {isHi ? 'प्रत्येक ३ माह का प्रभाव एवं मार्गदर्शन' : 'Detailed 3-month breakdown & guidance'}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {synthesis.quarters.map((q) => (
            <div
              key={q.quarterId}
              id={`quarter-card-${q.quarterId}`}
              className="p-4 sm:p-5 rounded-xl bg-[#FAF6ED] border border-astro-border hover:border-astro-accent flex flex-col justify-between transition-all shadow-2xs hover:shadow-xs space-y-3"
            >
              <div>
                {/* Quarter Title & Score Badge */}
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-astro-accent" />
                    <span className="font-bold text-sm text-astro-text">
                      {isHi ? q.labelHi : q.labelEn}
                    </span>
                  </div>
                  <span className={`px-2.5 py-0.5 text-xs font-bold rounded-full border ${getBadgeStyle(q.classification)}`}>
                    {q.netScore}%
                  </span>
                </div>

                {/* Date range & Dasha / Cosmic cycle info */}
                <div className="text-[11px] text-astro-secondary-text mb-3 font-medium">
                  {q.startDate} → {q.endDate} • {q.mahadashaLord} MD / {q.antardashaLord} AD
                </div>

                {/* Planetary factors */}
                <div className="space-y-1.5 text-xs">
                  {q.supportingFactors.slice(0, 1).map((factor, fIdx) => (
                    <div key={fIdx} className="flex items-start gap-1.5 text-astro-primary font-medium">
                      <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5 text-emerald-600" />
                      <span>{isHi ? q.supportingFactorsHi[fIdx] || factor : factor}</span>
                    </div>
                  ))}
                  {q.limitingFactors.slice(0, 1).map((factor, fIdx) => (
                    <div key={fIdx} className="flex items-start gap-1.5 text-astro-secondary-text">
                      <ChevronRight className="w-3.5 h-3.5 shrink-0 mt-0.5 text-astro-accent" />
                      <span>{isHi ? q.limitingFactorsHi[fIdx] || factor : factor}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actionable Practical Guidance */}
              <div className="pt-2.5 border-t border-astro-border text-xs text-astro-text leading-relaxed">
                <span className="font-bold text-astro-primary">
                  {isHi ? 'व्यावहारिक मार्गदर्शन: ' : 'Practical Guidance: '}
                </span>
                <span className="text-astro-secondary-text">
                  {isHi ? q.actionableGuidanceHi : q.actionableGuidance}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

